import axios from 'axios';
import { appInfo } from '../config/appInfo.const';

export interface ServiceImageState {
  id?: number;
  file: File | string;
  isDefault: boolean;
  raw_path?: string;
}

export interface BackendImagePayload {
  id: number | null;
  file_path: string;
  file_size_kb: number | null;
  file_type: string | null;
  is_default: boolean;
  image_url?: string;   // Added to capture the live absolute domain string
  image_file?: string;  // Matches your exact expected output structure
}

/**
 * Centrally process and map UI image states to backend-ready JSON payloads.
 * Handles uploading new files to the Port 2828 File Service automatically.
 */
export async function processAndMapImages(
  images: ServiceImageState[],
  bizCode: string
): Promise<BackendImagePayload[]> {
  
  const fileServiceForm = new FormData();
  fileServiceForm.append('app_name', 'sparlon');
  fileServiceForm.append('tenant_id', `biz_${bizCode}`);
  fileServiceForm.append('module', 'service_items');
  fileServiceForm.append('rules', JSON.stringify({ manipulations: { width: 600 } }));

  let hasFilesToUpload = false;

  // Append files and maintain a traceable index key mapping
  images.forEach((img, idx) => {
    if (img.file instanceof File) {
      fileServiceForm.append(`files[img_${idx}]`, img.file);
      hasFilesToUpload = true;
    }
  });

  const uploadedAssetsMap: Record<
    string, 
    { relative_path: string; absolute_url: string; size_kb: number; file_type: string }
    > = {};

if (hasFilesToUpload) {
  const uploadRes = await axios.post(appInfo.uploadImages, fileServiceForm, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });

  if (uploadRes.data?.status === 'success') {
    uploadRes.data.data.forEach((asset: any) => {
      // FIX: Map the incoming absolute_url property here!
      uploadedAssetsMap[asset.row_id] = {
        relative_path: asset.relative_path,
        absolute_url: asset.absolute_url, // <--- Captured cleanly now
        size_kb: asset.size_kb,
        file_type: asset.file_type
      };
    });
  } else {
    throw new Error("Central file microservice processing failed.");
  }
}

// Compile unified manifest
return images.map((img, idx) => {
  const dynamicKey = `img_${idx}`;

  if (img.file instanceof File && uploadedAssetsMap[dynamicKey]) {
    const uploadedAsset = uploadedAssetsMap[dynamicKey];
    return {
      id: null,
      file_path: uploadedAsset.relative_path,
      file_size_kb: uploadedAsset.size_kb,
      file_type: uploadedAsset.file_type,
      is_default: img.isDefault,
      
      // FIX: Feed the clean live file-service URL straight to your payload keys
      image_url: uploadedAsset.absolute_url,
      image_file: uploadedAsset.absolute_url
    };
  } else {
    // Standard structural fallback for non-modified, pre-existing images
    const fallbackPath = (img.raw_path || img.file) as string;
    
    return {
      id: img.id || null,
      file_path: fallbackPath,
      file_size_kb: null,
      file_type: null,
      is_default: img.isDefault,
      image_url: fallbackPath,
      image_file: fallbackPath
    };
  }
});
}